import type { ProjectWithDetails, StoryElement } from '@historyjki/shared';
import { Badge } from '../../../ui/Badge';
import { r2Url } from '../../../../utils/r2Url';

interface ReferencesStepProps {
  project: ProjectWithDetails;
  selectedIds: string[];
  onToggle: (id: string) => void;
  variantCount: number;
  onVariantCountChange: (count: number) => void;
}

const MAX_PICKS = 5;
const VARIANT_CHOICES = [1, 2, 3];

const KIND_LABEL: Record<StoryElement['kind'], string> = {
  character: 'Character',
  place: 'Place',
  object: 'Object',
};

const KIND_BADGE: Record<StoryElement['kind'], 'terra' | 'plum' | 'sage'> = {
  character: 'terra',
  place: 'plum',
  object: 'sage',
};

function firstRefImageKey(
  project: ProjectWithDetails,
  element: StoryElement,
): string | null {
  const matching = (project.reference_images || [])
    .filter((r) => r.owner_type === 'story_element' && r.owner_id === element.id)
    .sort((a, b) => a.uploaded_at.localeCompare(b.uploaded_at));
  return matching[0]?.r2_key ?? null;
}

export function ReferencesStep({
  project,
  selectedIds,
  onToggle,
  variantCount,
  onVariantCountChange,
}: ReferencesStepProps) {
  const elements = project.story_elements || [];
  const atCap = selectedIds.length >= MAX_PICKS;

  return (
    <div className="refine-step refine-step--references">
      <div className="refine-step__variant-count">
        <span className="refine-step__variant-count-label">Alternatives</span>
        <div
          className="refine-step__variant-count-seg"
          role="radiogroup"
          aria-label="Number of alternatives to generate"
        >
          {VARIANT_CHOICES.map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={variantCount === n}
              className={
                'refine-step__variant-count-opt' +
                (variantCount === n ? ' refine-step__variant-count-opt--on' : '')
              }
              onClick={() => onVariantCountChange(n)}
            >
              {n}
            </button>
          ))}
        </div>
        <span className="refine-step__variant-count-hint">
          Generate up to 3 at once and pick the best.
        </span>
      </div>

      <h3 className="refine-step__title">
        Select up to {MAX_PICKS} references ({selectedIds.length}/{MAX_PICKS})
      </h3>
      <p className="refine-step__help">
        The current image is always attached as the primary reference. Pick
        additional story elements whose visual descriptions and reference
        images should guide the refinement.
      </p>

      {elements.length === 0 ? (
        <p className="refine-step__empty">
          No story elements defined in this project. You can proceed without
          extra references — only the current image will be attached.
        </p>
      ) : (
        <div className="refine-step__ref-grid">
          {elements.map((el) => {
            const selected = selectedIds.includes(el.id);
            const disabled = !selected && atCap;
            const refKey = firstRefImageKey(project, el);
            return (
              <button
                key={el.id}
                type="button"
                className={
                  'refine-step__ref-card' +
                  (selected ? ' refine-step__ref-card--selected' : '') +
                  (disabled ? ' refine-step__ref-card--disabled' : '')
                }
                onClick={() => {
                  if (disabled) return;
                  onToggle(el.id);
                }}
                disabled={disabled}
                aria-pressed={selected}
                title={
                  disabled
                    ? `Remove another pick to select this (cap is ${MAX_PICKS}).`
                    : selected
                      ? `Click to unselect ${el.name}`
                      : `Click to pick ${el.name} as a reference`
                }
              >
                <div className="refine-step__ref-thumb">
                  {refKey ? (
                    <img
                      src={r2Url(refKey)}
                      alt={el.name}
                      className="refine-step__ref-thumb-img"
                    />
                  ) : (
                    <div className="refine-step__ref-thumb-placeholder">
                      {el.name.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="refine-step__ref-meta">
                  <Badge variant={KIND_BADGE[el.kind]}>{KIND_LABEL[el.kind]}</Badge>
                  <span className="refine-step__ref-name">{el.name}</span>
                </div>
                {selected && (
                  <span className="refine-step__ref-check" aria-hidden="true">
                    &#10003;
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
